import { useState, useEffect } from 'react';
import { pwaService, PWACapabilities } from '../services/PWAService';

/**
 * React hook for PWA functionality
 * Provides access to PWA capabilities and methods
 */
export function usePWA() {
  const [capabilities, setCapabilities] = useState<PWACapabilities>(
    pwaService.instance.getCapabilities()
  );
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Set up app navigation behavior
    pwaService.instance.setupAppNavigation();

    // Check for updates periodically
    const checkUpdates = async () => {
      const hasUpdate = await pwaService.instance.checkForUpdates();
      setUpdateAvailable(hasUpdate);
    };

    // Check for updates on mount and every 30 minutes
    checkUpdates();
    const updateInterval = setInterval(checkUpdates, 30 * 60 * 1000);

    // Listen for capability changes
    const handleCapabilityChange = () => {
      setCapabilities(pwaService.instance.getCapabilities());
    };

    // Listen for install events
    window.addEventListener('beforeinstallprompt', handleCapabilityChange);
    window.addEventListener('appinstalled', handleCapabilityChange);

    return () => {
      clearInterval(updateInterval);
      window.removeEventListener('beforeinstallprompt', handleCapabilityChange);
      window.removeEventListener('appinstalled', handleCapabilityChange);
    };
  }, []);

  const install = async (): Promise<boolean> => {
    const success = await pwaService.instance.showInstallPrompt();
    if (success) {
      setCapabilities(pwaService.instance.getCapabilities());
    }
    return success;
  };

  const applyUpdate = (): void => {
    pwaService.instance.applyUpdate();
  };

  const share = async (shareData: {
    title?: string;
    text?: string;
    url?: string;
  }): Promise<boolean> => {
    return pwaService.instance.shareContent(shareData);
  };

  const updateThemeColor = (color: string): void => {
    pwaService.instance.updateThemeColor(color);
  };

  const getLaunchMode = (): string | null => {
    return pwaService.instance.getLaunchMode();
  };

  const getAppTheme = (): 'light' | 'dark' => {
    return pwaService.instance.getAppTheme();
  };

  return {
    capabilities,
    updateAvailable,
    install,
    applyUpdate,
    share,
    updateThemeColor,
    getLaunchMode,
    getAppTheme,
  };
}